import React, { useState, useEffect } from "react";
import Card from "react-bootstrap/Card";
import {
    Typography,
    Tooltip,
    Grid,
    Button,
    LinearProgress,
    DialogContent,
    FormControl,
    Box,
    Autocomplete,
    TextField
} from "@mui/material";
import { Helmet } from "react-helmet";
import { useSelector } from "react-redux";
import { useData } from "@pure-ptr/react";
import { makeStyles } from "@mui/styles";

import { Portlet, PortletBody } from "../../../partials/content/Portlet";
import EditCreditLimitsDialog from "./EditCreditLimitsDialog";

import { RootState } from "../../../store/reducers";
import {
    getMonthlyLimits,
    editMonthlyLimits
} from "../../../store/api/creditUsage";
import { errorToastify } from "../../../util/toast";
import { Loadable } from "../../../common";

const useStyles = makeStyles(theme => ({
    card: {
        '@media (min-width:1200px)': {
                minWidth: 700,
            },
        marginTop: 40
    },
    filterFormControl: {
        margin: theme.spacing(2),
        minWidth: 220,
        maxWidth: 280
    },
    cardHeader: {
        display: "flex",
        justifyContent: "space-between"
    },
    gridLabel: {
        marginBottom: 0
    },
    creditsHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 8
    },
    creditsUsed: {
        color: "#8BC34A",
        fontWeight: 600,
        fontSize: 18,
        lineHeight: 1.2,
        marginTop: "7px"
    },
    creditsLeft: {
        color: "#888",
        fontSize: 10
    },
    progressBarWrapper: {
        position: "relative",
        marginTop: 8
    },
    progressBar: {
        height: 14,
        borderRadius: 7,
        backgroundColor: "#e0e0e0"
    },
    progressRoot: {
        backgroundColor: "#e0e0e0",
        borderRadius: 7
    },
    progressBarColor: {
        backgroundColor: "#8BC34A",
        borderRadius: 7
    },
    dot: {
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        width: 8,
        height: 8,
        borderRadius: "50%",
        boxShadow: "0 0 2px rgba(0,0,0,0.1)",
        zIndex: 2,
        transition: "background 0.3s"
    },
    dotWhite: {
        background: "#fff"
    },
    dotGreen: {
        background: "#8BC34A"
    },
    label: {
        color: "#888",
        fontSize: 10,
        marginBottom: 2
    },
    creditsComponent: {
        border: "1px solid rgba(0, 0, 0, 0.12)",
        maxWidth: "430px"
    }
}));

export const CreditsUsagePage = () => {
    const classes = useStyles();
    const [openDialog, setOpenDialog] = useState({ AI: false, EAPG: false, SYSTEM: false, CLAIMNOTE: false });
    const [creditEditPayload, setCreditEditPayload] = useState({});
    const [userSelectedClient, setUserSelectedClient] = useState(null);

    const clients: any = useSelector<RootState>(
        state => state.clients.tinyList?.filter((item)=>item?.active)
    );
    let selectedClient: any = useSelector<RootState>(
        state => state.dashboards.selectedClient
    );
    const currentSelectedClient =
        userSelectedClient ||
        clients?.find(client => client.clientId === selectedClient)?.clientId ||
        "";

    const {
        data: monthlyLimits,
        isPending,
        error: getLimitsError,
        reload: reloadMonthlyLimits
    } = useData(async () => {
        if (!selectedClient && !userSelectedClient) return;
        const monthlyLimits = await getMonthlyLimits(
            userSelectedClient || selectedClient
        );
        return monthlyLimits;
    }, [userSelectedClient, selectedClient]);

    const {
        data,
        isPending: isPendingUpdate,
        error: updateError
    } = useData(async () => {
        if (Object.keys(creditEditPayload).length <= 0) return;
        const editResponse = await editMonthlyLimits(creditEditPayload);
        handleCloseDialog();
        setCreditEditPayload({});
        reloadMonthlyLimits();
        return editResponse;
    }, [creditEditPayload]);

    useEffect(() => {
        if (updateError) {
            setCreditEditPayload({});
            errorToastify(
                updateError?.payload?.error ||
                    "Failed to update monthly limits, Please contact Support"
            );
        }
    }, [updateError]);

    const claimNoteMonthlyTransactions = monthlyLimits?.find(
        (item) => item?.systemName === "CLAIMNOTE",
    );
    const eapgMonthlyTransactions = monthlyLimits?.find(
        item => item?.systemName === "EAPG"
    );
    const aIMonthlyTransactions = monthlyLimits?.find(
        item => item?.systemName === "AI"
    );
    const systemMonthlyTransactions = monthlyLimits?.find(
        item => item?.systemName === "SYSTEM"
    );

    const numDots = 4;
    const dotPositions = Array.from(
        { length: numDots },
        (_, i) => (i + 1) / (numDots + 1)
    );

    // Calculate progress for AI and EAPG
    const aiProgress = aIMonthlyTransactions?.totalLimit
        ? (aIMonthlyTransactions.usedCredits /
              aIMonthlyTransactions.totalLimit) *
          100
        : 0;

    const eapgProgress = eapgMonthlyTransactions?.totalLimit
        ? (eapgMonthlyTransactions.usedCredits /
              eapgMonthlyTransactions.totalLimit) *
          100
        : 0;
    const claimNotesProgress = claimNoteMonthlyTransactions?.totalLimit
        ? (claimNoteMonthlyTransactions.usedCredits /
              claimNoteMonthlyTransactions.totalLimit) *
          100
        : 0;

    const systemProgress = systemMonthlyTransactions?.totalLimit
        ? (systemMonthlyTransactions.usedCredits /
              systemMonthlyTransactions.totalLimit) *
          100
        : 0;

    const handleOpenDialog = key => {
        setOpenDialog({ ...openDialog, [key]: true });
    };
    const handleCloseDialog = () => {
        setOpenDialog({ AI: false, EAPG: false, SYSTEM: false, CLAIMNOTE: false });
    };

    const handleClientChange = clientId => {
        setUserSelectedClient(clientId);
    };

    return (
        <Loadable loading={isPending != null}>
            <Helmet title="Credit Management" />
            <Portlet>
                <PortletBody>
                    <Box display="flex" justifyContent="space-between">
                        <Box>
                            <Card className={classes.card}>
                                <Card.Header className={classes.cardHeader}>
                                    <Typography variant="h6">
                                        AI (
                                        {aIMonthlyTransactions?.totalLimit || 0}
                                        )
                                    </Typography>
                                    <Tooltip title="Edit AI Monthly Credits">
                                        <Typography component="div">
                                            <Grid
                                                component="label"
                                                container
                                                alignItems="center"
                                                spacing={0}
                                                className={classes.gridLabel}
                                            >
                                                <Grid item>
                                                    <Button
                                                        onClick={() =>
                                                            handleOpenDialog(
                                                                "AI"
                                                            )
                                                        }
                                                        variant="contained"
                                                    >
                                                        Change Limits
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Typography>
                                    </Tooltip>
                                </Card.Header>
                                <Card.Body>
                                    <DialogContent
                                        dividers
                                        className={classes.creditsComponent}
                                    >
                                        <Typography
                                            className={classes.label}
                                            variant="caption"
                                        >
                                            AI Monthly Credits
                                        </Typography>
                                        <div className={classes.creditsHeader}>
                                            <Typography
                                                className={classes.creditsUsed}
                                            >
                                                {aIMonthlyTransactions?.usedCredits ||
                                                    0}{" "}
                                                Credits Used
                                            </Typography>
                                            <Typography
                                                className={classes.creditsLeft}
                                            >
                                                {aIMonthlyTransactions
                                                    ? aIMonthlyTransactions?.totalLimit -
                                                      aIMonthlyTransactions?.usedCredits
                                                    : 0}{" "}
                                                Credits Left
                                            </Typography>
                                        </div>
                                        <div
                                            className={
                                                classes.progressBarWrapper
                                            }
                                        >
                                            <LinearProgress
                                                variant="determinate"
                                                value={aiProgress}
                                                classes={{
                                                    root: classes.progressRoot,
                                                    bar:
                                                        classes.progressBarColor
                                                }}
                                                className={classes.progressBar}
                                            />
                                            {dotPositions.map((pos, idx) => {
                                                const isFilled =
                                                    aiProgress > 0 &&
                                                    pos * 100 <= aiProgress;
                                                return (
                                                    <span
                                                        key={idx}
                                                        className={`${
                                                            classes.dot
                                                        } ${
                                                            isFilled
                                                                ? classes.dotWhite
                                                                : classes.dotGreen
                                                        }`}
                                                        style={{
                                                            left: `${pos *
                                                                100}%`
                                                        }}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </DialogContent>
                                </Card.Body>
                            </Card>

                            <Card className={classes.card}>
                                <Card.Header className={classes.cardHeader}>
                                    <Typography variant="h6">
                                        EAPG (
                                        {eapgMonthlyTransactions?.totalLimit ||
                                            0}
                                        )
                                    </Typography>
                                    <Tooltip title="Edit EAPG Monthly Transactions">
                                        <Typography component="div">
                                            <Grid
                                                component="label"
                                                container
                                                alignItems="center"
                                                spacing={0}
                                                className={classes.gridLabel}
                                            >
                                                <Grid item>
                                                    <Button
                                                        onClick={() =>
                                                            handleOpenDialog(
                                                                "EAPG"
                                                            )
                                                        }
                                                        variant="contained"
                                                    >
                                                        Change Limits
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Typography>
                                    </Tooltip>
                                </Card.Header>
                                <Card.Body>
                                    <DialogContent
                                        dividers
                                        className={classes.creditsComponent}
                                    >
                                        <Typography
                                            className={classes.label}
                                            variant="caption"
                                        >
                                            EAPG Monthly Transactions
                                        </Typography>
                                        <div className={classes.creditsHeader}>
                                            <Typography
                                                className={classes.creditsUsed}
                                            >
                                                {eapgMonthlyTransactions?.usedCredits ||
                                                    0}{" "}
                                                Transactions Used
                                            </Typography>
                                            <Typography
                                                className={classes.creditsLeft}
                                            >
                                                {eapgMonthlyTransactions
                                                    ? eapgMonthlyTransactions?.totalLimit -
                                                      eapgMonthlyTransactions?.usedCredits
                                                    : 0}{" "}
                                                Transactions Left
                                            </Typography>
                                        </div>
                                        <div
                                            className={
                                                classes.progressBarWrapper
                                            }
                                        >
                                            <LinearProgress
                                                variant="determinate"
                                                value={eapgProgress}
                                                classes={{
                                                    root: classes.progressRoot,
                                                    bar:
                                                        classes.progressBarColor
                                                }}
                                                className={classes.progressBar}
                                            />
                                            {dotPositions.map((pos, idx) => {
                                                const isFilled =
                                                    eapgProgress > 0 &&
                                                    pos * 100 <= eapgProgress;
                                                return (
                                                    <span
                                                        key={idx}
                                                        className={`${
                                                            classes.dot
                                                        } ${
                                                            isFilled
                                                                ? classes.dotWhite
                                                                : classes.dotGreen
                                                        }`}
                                                        style={{
                                                            left: `${pos *
                                                                100}%`
                                                        }}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </DialogContent>
                                </Card.Body>
                            </Card>

                            <Card className={classes.card}>
                                <Card.Header className={classes.cardHeader}>
                                    <Typography variant="h6">
                                        SYSTEM (
                                        {systemMonthlyTransactions?.totalLimit ||
                                            0}
                                        )
                                    </Typography>
                                    <Tooltip title="Edit System Monthly Credits">
                                        <Typography component="div">
                                            <Grid
                                                component="label"
                                                container
                                                alignItems="center"
                                                spacing={0}
                                                className={classes.gridLabel}
                                            >
                                                <Grid item>
                                                    <Button
                                                        onClick={() =>
                                                            handleOpenDialog(
                                                                "SYSTEM"
                                                            )
                                                        }
                                                        variant="contained"
                                                    >
                                                        Change Limits
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Typography>
                                    </Tooltip>
                                </Card.Header>
                                <Card.Body>
                                    <DialogContent
                                        dividers
                                        className={classes.creditsComponent}
                                    >
                                        <Typography
                                            className={classes.label}
                                            variant="caption"
                                        >
                                            System Monthly Credits
                                        </Typography>
                                        <div className={classes.creditsHeader}>
                                            <Typography
                                                className={classes.creditsUsed}
                                            >
                                                {systemMonthlyTransactions?.usedCredits ||
                                                    0}{" "}
                                                Credits Used
                                            </Typography>
                                            <Typography
                                                className={classes.creditsLeft}
                                            >
                                                {systemMonthlyTransactions
                                                    ? systemMonthlyTransactions?.totalLimit -
                                                      systemMonthlyTransactions?.usedCredits
                                                    : 0}{" "}
                                                Credits Left
                                            </Typography>
                                        </div>
                                        <div
                                            className={
                                                classes.progressBarWrapper
                                            }
                                        >
                                            <LinearProgress
                                                variant="determinate"
                                                value={systemProgress}
                                                classes={{
                                                    root: classes.progressRoot,
                                                    bar:
                                                        classes.progressBarColor
                                                }}
                                                className={classes.progressBar}
                                            />
                                            {dotPositions.map((pos, idx) => {
                                                const isFilled =
                                                    systemProgress > 0 &&
                                                    pos * 100 <= systemProgress;
                                                return (
                                                    <span
                                                        key={idx}
                                                        className={`${
                                                            classes.dot
                                                        } ${
                                                            isFilled
                                                                ? classes.dotWhite
                                                                : classes.dotGreen
                                                        }`}
                                                        style={{
                                                            left: `${pos *
                                                                100}%`
                                                        }}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </DialogContent>
                                </Card.Body>
                            </Card>
                            <Card className={classes.card}>
                                <Card.Header className={classes.cardHeader}>
                                    <Typography variant="h6">
                                        CLAIM NOTES (
                                        {claimNoteMonthlyTransactions?.totalLimit ||
                                            0}
                                        )
                                    </Typography>
                                    <Tooltip title="Edit Claim Note Monthly Transactions">
                                        <Typography component="div">
                                            <Grid
                                                component="label"
                                                container
                                                alignItems="center"
                                                spacing={0}
                                                className={classes.gridLabel}
                                            >
                                                <Grid item>
                                                    <Button
                                                        onClick={() =>
                                                            handleOpenDialog(
                                                                "CLAIMNOTE",
                                                            )
                                                        }
                                                        variant="contained"
                                                    >
                                                        Change Limits
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Typography>
                                    </Tooltip>
                                </Card.Header>
                                <Card.Body>
                                    <DialogContent
                                        dividers
                                        className={classes.creditsComponent}
                                    >
                                        <Typography
                                            className={classes.label}
                                            variant="caption"
                                        >
                                            Claim Note Monthly Transactions
                                        </Typography>
                                        <div className={classes.creditsHeader}>
                                            <Typography
                                                className={classes.creditsUsed}
                                            >
                                                {claimNoteMonthlyTransactions?.usedCredits ||
                                                    0}{" "}
                                                Transactions Used
                                            </Typography>
                                            <Typography
                                                className={classes.creditsLeft}
                                            >
                                                {claimNoteMonthlyTransactions
                                                    ? claimNoteMonthlyTransactions?.totalLimit -
                                                      claimNoteMonthlyTransactions?.usedCredits
                                                    : 0}{" "}
                                                Transactions Left
                                            </Typography>
                                        </div>
                                        <div
                                            className={
                                                classes.progressBarWrapper
                                            }
                                        >
                                            <LinearProgress
                                                variant="determinate"
                                                value={claimNotesProgress}
                                                classes={{
                                                    root: classes.progressRoot,
                                                    bar: classes.progressBarColor,
                                                }}
                                                className={classes.progressBar}
                                            />
                                            {dotPositions.map((pos, idx) => {
                                                const isFilled =
                                                    claimNotesProgress > 0 &&
                                                    pos * 100 <=
                                                        claimNotesProgress;
                                                return (
                                                    <span
                                                        key={idx}
                                                        className={`${
                                                            classes.dot
                                                        } ${
                                                            isFilled
                                                                ? classes.dotWhite
                                                                : classes.dotGreen
                                                        }`}
                                                        style={{
                                                            left: `${
                                                                pos * 100
                                                            }%`,
                                                        }}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </DialogContent>
                                </Card.Body>
                            </Card>
                        </Box>
                        <FormControl className={classes.filterFormControl}>
                            <Autocomplete
                                options={clients || []}
                                getOptionLabel={option => option?.name || ""}
                                value={
                                    clients?.find(
                                        client =>
                                            client.clientId ===
                                            currentSelectedClient
                                    ) || null
                                }
                                onChange={(_, newValue) =>
                                    handleClientChange(
                                        newValue ? newValue.clientId : ""
                                    )
                                }
                                renderInput={params => (
                                    <TextField {...params} label="Client" />
                                )}
                                isOptionEqualToValue={(option, value) =>
                                    option.clientId === value.clientId
                                }
                                disableClearable
                            />
                        </FormControl>
                    </Box>
                </PortletBody>
                <EditCreditLimitsDialog
                    open={
                        openDialog?.AI ||
                        openDialog?.EAPG ||
                        openDialog?.SYSTEM ||
                        openDialog?.CLAIMNOTE
                    }
                    systemType={
                        openDialog?.EAPG
                            ? "EAPG"
                            : openDialog?.SYSTEM
                              ? "SYSTEM"
                              : openDialog?.CLAIMNOTE
                                ? "CLAIMNOTE"
                                : "AI"
                    }
                    usedCredits={
                        openDialog?.EAPG
                            ? eapgMonthlyTransactions?.usedCredits
                            : openDialog?.SYSTEM
                              ? systemMonthlyTransactions?.usedCredits
                              : openDialog?.CLAIMNOTE
                                ? claimNoteMonthlyTransactions?.usedCredits
                                : aIMonthlyTransactions?.usedCredits
                    }
                    handleClose={handleCloseDialog}
                    onSave={(data) => setCreditEditPayload({ ...data })}
                    selectedClient={currentSelectedClient}
                    loading={isPendingUpdate != null}
                    defaultValue={
                        openDialog?.EAPG
                            ? eapgMonthlyTransactions?.totalLimit || 0
                            : openDialog?.SYSTEM
                              ? systemMonthlyTransactions?.totalLimit || 0
                              : openDialog?.CLAIMNOTE
                                ? claimNoteMonthlyTransactions?.totalLimit || 0
                                : aIMonthlyTransactions?.totalLimit || 0
                    }
                />
            </Portlet>
        </Loadable>
    );
};
